import React, { createContext, useContext, useState } from 'react';

import differenceBy from 'lodash/differenceBy';
import unionBy from 'lodash/unionBy';

import { Folder, RepositoryIssue } from '@qality/common-model';

import { useTestCaseRepositoryFoldersContext } from '../TestCaseRepositoryFoldersProvider';
import { Key, useIssuesSelection } from './useIssuesSelection';

export type TestCaseRepositorySelectionContextValue = Pick<
  ReturnType<typeof useIssuesSelection>,
  | 'getSelectedIssues'
  | 'clearSelectedIssues'
  | 'clearAllSelectedIssues'
  | 'getKey'
> & {
  getSelectedIssues: (key: Key) => Array<RepositoryIssue>;
  getSelectedIssuesIds: (key: Key) => Array<string | number>;

  allSelectedIssues: Array<RepositoryIssue>;
  allSelectedIssuesIds: Array<RepositoryIssue['id']>;

  clearSelectedIssues: (key: Key) => void;
  clearAllSelectedIssues: () => void;

  isIssueSelected: (key: Key, issue: RepositoryIssue) => boolean;
  isAnyIssueSelected: boolean;
  isIssueSelectedAnywhere: (issue: RepositoryIssue) => boolean;

  toggleSelectIssue: (
    key: Key,
    toggledIssue: RepositoryIssue,
    folder?: Folder
  ) => void;
  toggleBulkSelectIssues: (
    key: Key,
    issues: Array<RepositoryIssue>,
    folder?: Folder
  ) => void;

  areAllIssuesSelected: (key: Key, issues: Array<RepositoryIssue>) => boolean;

  getKey: (type: 'all-test-cases' | 'folder', key?: Folder['id']) => Key;

  numberOfSelectedIssuesInsideSubfolders: number;
};

export const initialContextValue: TestCaseRepositorySelectionContextValue = {
  getSelectedIssues: () => undefined,
  getSelectedIssuesIds: () => undefined,

  allSelectedIssues: [],
  allSelectedIssuesIds: [],

  isAnyIssueSelected: false,

  clearSelectedIssues: () => undefined,
  clearAllSelectedIssues: () => undefined,
  isIssueSelectedAnywhere: () => undefined,

  toggleSelectIssue: () => undefined,
  toggleBulkSelectIssues: () => undefined,

  isIssueSelected: () => false,
  areAllIssuesSelected: () => false,

  getKey: () => undefined,
  numberOfSelectedIssuesInsideSubfolders: 0,
};

export const TestCaseRepositorySelectionContext =
  createContext<TestCaseRepositorySelectionContextValue>(initialContextValue);

export const useTestCaseRepositorySelectionContext =
  (): TestCaseRepositorySelectionContextValue => {
    return useContext(TestCaseRepositorySelectionContext);
  };

type Props = { children: React.ReactNode };

export function TestCaseRepositorySelectionProvider({
  children,
}: Props): JSX.Element {
  const {
    selectIssues,

    getSelectedIssues,
    getAllSelectedIssues,

    hasSelectedIssues,

    clearSelectedIssues,
    clearAllSelectedIssues,

    isAnyIssueSelected,

    isKeyAllTestCases,
    getKey,
  } = useIssuesSelection();

  const { currentFolder } = useTestCaseRepositoryFoldersContext();

  // This is just workaround until we have issue with bulk delete inside subfolders fixed
  const [
    numberOfSelectedIssuesInsideSubfolders,
    setNumberOfSelectedIssuesInsideSubfolders,
  ] = useState<number>(0);

  const toggleSelectIssue = (
    key: Key,
    issue: RepositoryIssue,
    folder?: Folder
  ): void => {
    const selectedIssues = getSelectedIssues(key);
    const isSelected = _isIssueSelected(key, issue, selectedIssues);

    const removeSelection = () => {
      if (folder !== null && folder.id !== currentFolder.id) {
        setNumberOfSelectedIssuesInsideSubfolders(
          numberOfSelectedIssuesInsideSubfolders - 1
        );
      }

      return isKeyAllTestCases(key)
        ? selectedIssues.filter((selectedIssue) => {
            return issue.id !== selectedIssue.id;
          })
        : selectedIssues.filter((selectedIssue) => {
            return (
              issue.testCaseInFolder.id !== selectedIssue.testCaseInFolder.id
            );
          });
    };

    const addSelection = () => {
      if (folder !== null && folder.id !== currentFolder.id) {
        setNumberOfSelectedIssuesInsideSubfolders(
          numberOfSelectedIssuesInsideSubfolders + 1
        );
      }

      return [...selectedIssues, issue];
    };

    const updatedSelectedIssues = isSelected
      ? removeSelection()
      : addSelection();

    selectIssues(key, updatedSelectedIssues);
  };

  const isPermitted = (issue: RepositoryIssue) => {
    return issue.id !== undefined;
  };

  const toggleBulkSelectIssues = (
    key: Key,
    issues: Array<RepositoryIssue>,
    folder?: Folder
  ): void => {
    if (issues.length === 0) {
      return;
    }

    const selectedIssues = getSelectedIssues(key);

    const areAllSelected = _areAllIssuesSelected(key, issues, selectedIssues);

    const valueIteratee: any = isKeyAllTestCases(key)
      ? 'id'
      : 'testCaseInFolder.id';

    const removeSelection = () => {
      if (folder !== null && folder.id !== currentFolder.id) {
        setNumberOfSelectedIssuesInsideSubfolders(
          numberOfSelectedIssuesInsideSubfolders - issues.length
        );
      }

      return differenceBy(selectedIssues, issues, valueIteratee);
    };

    const addSelection = () => {
      if (folder !== null && folder.id !== currentFolder.id) {
        setNumberOfSelectedIssuesInsideSubfolders(
          numberOfSelectedIssuesInsideSubfolders + issues.length
        );
      }

      return unionBy(selectedIssues, issues, valueIteratee).filter(isPermitted);
    };

    const updatedIssues = areAllSelected ? removeSelection() : addSelection();

    selectIssues(key, updatedIssues);
  };

  const isIssueSelected = (key: Key, issue: RepositoryIssue): boolean => {
    if (!hasSelectedIssues(key)) {
      return false;
    }

    const selectedIssues = getSelectedIssues(key);
    return _isIssueSelected(key, issue, selectedIssues);
  };

  const _isIssueSelected = (
    key: Key,
    issue: RepositoryIssue,
    selectedIssues: Array<RepositoryIssue>
  ) => {
    const isIssueSelectedInAllTestCases = () => {
      return selectedIssues.some((selectedIssue) => {
        return selectedIssue.id === issue.id;
      });
    };

    const isIssueSelectedInFolder = () => {
      return selectedIssues.some((selectedIssue) => {
        return selectedIssue.testCaseInFolder.id === issue.testCaseInFolder.id;
      });
    };

    return isKeyAllTestCases(key)
      ? isIssueSelectedInAllTestCases()
      : isIssueSelectedInFolder();
  };

  const areAllIssuesSelected = (
    key: Key,
    issues: Array<RepositoryIssue>
  ): boolean => {
    if (issues.length === 0) {
      return false;
    }

    const selectedIssues = getSelectedIssues(key);
    return _areAllIssuesSelected(key, issues, selectedIssues);
  };

  const _areAllIssuesSelected = (
    key: Key,
    issues: Array<RepositoryIssue>,
    selectedIssues: Array<RepositoryIssue>
  ) => {
    return issues.filter(isPermitted).every((issue) => {
      return _isIssueSelected(key, issue, selectedIssues);
    });
  };

  const isIssueSelectedAnywhere = (issue: RepositoryIssue) => {
    return getAllSelectedIssuesIds().includes(issue.id);
  };

  const getSelectedIssuesIds = (key: Key): Array<RepositoryIssue['id']> => {
    return getSelectedIssues(key).map((issue) => issue.id);
  };

  const getAllSelectedIssuesIds = (): Array<RepositoryIssue['id']> => {
    return getAllSelectedIssues().map((issue) => issue.id);
  };

  return (
    <TestCaseRepositorySelectionContext.Provider
      value={{
        getSelectedIssues,
        getSelectedIssuesIds,

        allSelectedIssues: getAllSelectedIssues(),
        allSelectedIssuesIds: getAllSelectedIssuesIds(),

        clearSelectedIssues,
        clearAllSelectedIssues,

        isIssueSelected,
        isAnyIssueSelected: isAnyIssueSelected(),
        isIssueSelectedAnywhere,

        toggleSelectIssue,
        toggleBulkSelectIssues,
        areAllIssuesSelected,

        getKey,
        numberOfSelectedIssuesInsideSubfolders,
      }}
    >
      {children}
    </TestCaseRepositorySelectionContext.Provider>
  );
}
